import React, { useState } from 'react';
import { ArrowRight, CheckCircle, Lock, Mail, UserRound } from 'lucide-react';
import { authService } from '../services/authService';

const initialSignup = {
  role: 'student',
  studentName: '',
  parentName: '',
  mobile: '',
  email: '',
  grade: '',
  city: '',
  schoolName: '',
  password: '',
  confirmPassword: '',
};

const initialLogin = {
  role: 'student',
  email: '',
  password: '',
};

const validateSignup = (form) => {
  const errors = {};
  if (!form.studentName.trim()) errors.studentName = 'Student name is required.';
  if (!form.parentName.trim()) errors.parentName = 'Parent name is required.';
  if (!/^\d{10}$/.test(form.mobile.trim())) errors.mobile = 'Enter a valid 10-digit mobile number.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = 'Enter a valid email.';
  if (form.role !== 'school' && !form.grade.trim()) errors.grade = 'Class/grade is required.';
  if (!form.city.trim()) errors.city = 'City is required.';
  if (['student', 'school'].includes(form.role) && !form.schoolName.trim()) errors.schoolName = 'School name is required.';
  if (form.password.length < 6) errors.password = 'Password must be at least 6 characters.';
  if (form.password !== form.confirmPassword) errors.confirmPassword = 'Passwords do not match.';
  return errors;
};

const validateLogin = (form) => {
  const errors = {};
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = 'Enter a valid email.';
  if (!form.password) errors.password = 'Password is required.';
  return errors;
};

const roleOptions = [
  ['student', 'Student', 'Create or manage your assessment journey.'],
  ['parent', 'Parent', 'Track plans, reports, and consultation updates.'],
  ['school', 'School / Bulk Test', 'Coordinate bulk assessments for your school.'],
];

const RoleSelector = ({ value, onChange, roles = ['student', 'parent', 'school'] }) => (
  <div className="auth-role-grid" role="radiogroup" aria-label="Choose role">
    {roleOptions.filter(([role]) => roles.includes(role)).map(([role, title, desc]) => (
      <button
        key={role}
        type="button"
        className={`auth-role-card ${value === role ? 'active' : ''}`}
        onClick={() => onChange(role)}
      >
        <UserRound size={17} />
        <strong>{title}</strong>
        <span>{desc}</span>
      </button>
    ))}
  </div>
);

const Field = ({ label, error, ...props }) => (
  <label className="auth-field">
    <span>{label}</span>
    <input {...props} />
    {error && <em>{error}</em>}
  </label>
);

const PasswordField = ({ label, error, value, onChange }) => {
  const [visible, setVisible] = useState(false);
  return (
    <label className="auth-field">
      <span>{label}</span>
      <div className="auth-password-wrap">
        <input type={visible ? 'text' : 'password'} value={value} onChange={onChange} />
        <button type="button" onClick={() => setVisible((current) => !current)}>
          {visible ? 'Hide' : 'Show'}
        </button>
      </div>
      {error && <em>{error}</em>}
    </label>
  );
};

const AuthView = ({ mode = 'login', onModeChange, onAuthenticated }) => {
  const [activeMode, setActiveMode] = useState(mode);
  const [signup, setSignup] = useState(initialSignup);
  const [login, setLogin] = useState(initialLogin);
  const [forgotEmail, setForgotEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const changeMode = (nextMode) => {
    setActiveMode(nextMode);
    setErrors({});
    setMessage('');
    onModeChange?.(nextMode);
  };

  const updateSignup = (key, value) => setSignup((current) => ({ ...current, [key]: value }));
  const updateLogin = (key, value) => setLogin((current) => ({ ...current, [key]: value }));

  const submitSignup = async (event) => {
    event.preventDefault();
    const validation = validateSignup(signup);
    setErrors(validation);
    if (Object.keys(validation).length) return;

    setLoading(true);
    try {
      const user = await authService.signup({
        ...signup,
        grade: signup.role === 'school' ? 'Bulk assessment' : signup.grade,
      });
      onAuthenticated?.(user);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const submitLogin = async (event) => {
    event.preventDefault();
    const validation = validateLogin(login);
    setErrors(validation);
    if (Object.keys(validation).length) return;

    setLoading(true);
    try {
      const user = await authService.login(login);
      onAuthenticated?.(user);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const result = await authService.forgotPassword(forgotEmail);
      setMessage(result.devResetUrl ? `${result.message} Dev reset link: ${result.devResetUrl}` : result.message);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="auth-page" aria-label="MindVrik authentication">
      <div className="auth-shell">
        <div className="auth-copy">
          <div className="section-label">MindVrik Access</div>
          <h1>Begin with a secure account.</h1>
          <p>
            Sign up as a student, parent, or school coordinator to continue toward plan selection,
            payment, assessment access, reports, and expert guidance.
          </p>
          <div className="auth-trust-list">
            {['Mock auth for development only', 'Backend-ready service layer', 'Protected dashboard route'].map((item) => (
              <span key={item}><CheckCircle size={15} />{item}</span>
            ))}
          </div>
        </div>

        <div className="auth-card">
          <div className="auth-tabs">
            <button className={activeMode === 'login' ? 'active' : ''} onClick={() => changeMode('login')}>Login</button>
            <button className={activeMode === 'signup' ? 'active' : ''} onClick={() => changeMode('signup')}>Signup</button>
          </div>

          {message && <div className="auth-message">{message}</div>}

          {activeMode === 'signup' && (
            <form onSubmit={submitSignup}>
              <RoleSelector value={signup.role} onChange={(role) => updateSignup('role', role)} />
              <div className="auth-form-grid">
                <Field label={signup.role === 'school' ? 'Coordinator Name' : 'Student Name'} value={signup.studentName} onChange={(e) => updateSignup('studentName', e.target.value)} error={errors.studentName} />
                <Field label={signup.role === 'school' ? 'Alternate Contact Name' : 'Parent Name'} value={signup.parentName} onChange={(e) => updateSignup('parentName', e.target.value)} error={errors.parentName} />
                <Field label="Mobile Number" value={signup.mobile} onChange={(e) => updateSignup('mobile', e.target.value)} error={errors.mobile} inputMode="numeric" />
                <Field label="Email" type="email" value={signup.email} onChange={(e) => updateSignup('email', e.target.value)} error={errors.email} />
                {['student', 'school'].includes(signup.role) && <Field label="School Name" value={signup.schoolName} onChange={(e) => updateSignup('schoolName', e.target.value)} error={errors.schoolName} />}
                {signup.role !== 'school' && <Field label="Class / Grade" value={signup.grade} onChange={(e) => updateSignup('grade', e.target.value)} error={errors.grade} />}
                <Field label="City" value={signup.city} onChange={(e) => updateSignup('city', e.target.value)} error={errors.city} />
                <PasswordField label="Password" value={signup.password} onChange={(e) => updateSignup('password', e.target.value)} error={errors.password} />
                <PasswordField label="Confirm Password" value={signup.confirmPassword} onChange={(e) => updateSignup('confirmPassword', e.target.value)} error={errors.confirmPassword} />
              </div>
              <button className="btn-primary auth-submit" type="submit" disabled={loading}>
                Create Account <ArrowRight size={15} />
              </button>
            </form>
          )}

          {activeMode === 'login' && (
            <form onSubmit={submitLogin}>
              <RoleSelector value={login.role} onChange={(role) => updateLogin('role', role)} />
              <Field label="Email" type="email" value={login.email} onChange={(e) => updateLogin('email', e.target.value)} error={errors.email} />
              <PasswordField label="Password" value={login.password} onChange={(e) => updateLogin('password', e.target.value)} error={errors.password} />
              <button className="btn-primary auth-submit" type="submit" disabled={loading}>
                <Lock size={15} /> Login
              </button>
              <button type="button" className="auth-link" onClick={() => changeMode('forgot')}>Forgot password?</button>
            </form>
          )}

          {activeMode === 'forgot' && (
            <form onSubmit={submitForgot}>
              <p className="auth-mini-copy">Enter your email and we will send a secure reset link if the account exists.</p>
              <Field label="Email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} />
              <button className="btn-primary auth-submit" type="submit" disabled={loading}>
                <Mail size={15} /> Request Reset
              </button>
              <button type="button" className="auth-link" onClick={() => changeMode('login')}>Back to login</button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default AuthView;
